import { useEffect, useMemo, useState } from "react";
import { api, downloadBlobResponse } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ProdiSelect from "@/components/ProdiSelect";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { formatWIB } from "@/lib/datetime";
import { seesAllProdi, isAdminKoas } from "@/lib/roles";
import StickyHeader from "@/components/StickyHeader";

const PHASE_LABEL = {
    preview: { text: "Preview", cls: "bg-slate-100 text-slate-600 border-slate-300" },
    commit: { text: "Final", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
};

export default function RiwayatImport() {
    const { user } = useAuth();
    const isSuper = seesAllProdi(user.role);
    const isKoas = isAdminKoas(user.role);

    const [logs, setLogs] = useState([]);
    const [prodiList, setProdiList] = useState([]);
    const [loading, setLoading] = useState(false);
    // module: "peserta" (default, sama seperti perilaku lama) | "orientasi_rs" | "all"
    // -- satu halaman & satu collection import_logs dipakai bersama utk kedua menu
    // (lihat backend record_import_log/_import_log_scope), dibedakan lewat filter ini.
    const [filter, setFilter] = useState({ prodi_id: "all", phase: "commit", module: "peserta" });
    const MODULE_LABEL = { peserta: "Peserta Didik", orientasi_rs: "Orientasi RS", all: "Semua Menu" };

    const prodiMap = useMemo(() => {
        const m = {};
        prodiList.forEach((p) => (m[p.id] = p));
        return m;
    }, [prodiList]);

    const load = async () => {
        setLoading(true);
        try {
            const params = { module: filter.module };
            if (isSuper && filter.prodi_id !== "all") params.prodi_id = filter.prodi_id;
            if (filter.phase !== "all") params.phase = filter.phase;
            const { data } = await api.get("/import-logs", { params });
            setLogs(data);
        } catch {
            toast.error("Gagal memuat riwayat import");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isSuper) api.get("/prodi").then(({ data }) => setProdiList(data)).catch(() => {});
    }, [isSuper]);
    useEffect(() => { load(); /* eslint-disable-line */ }, [filter.prodi_id, filter.phase, filter.module]);

    const downloadFailed = async (log) => {
        try {
            const res = await api.get(`/import-logs/${log.id}/failed-export`, { responseType: "blob" });
            const fallback = `aura_riwayat-import_${(log.filename || "import").replace(/\.[^.]+$/, "")}.xlsx`;
            downloadBlobResponse(res, fallback);
        } catch {
            toast.error("Gagal mengunduh log");
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-4">
            <StickyHeader>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{MODULE_LABEL[filter.module]}</div>
                    <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                        Riwayat Import
                    </h1>
                    <p className="text-sm text-slate-500 mt-1">
                        {isSuper ? "Riwayat batch import Excel semua prodi." : isKoas && !user.prodi_id ? "Riwayat batch import Excel Peserta Koas (semua modul Koas)." : isKoas ? "Riwayat batch import Excel modul anda." : "Riwayat batch import Excel prodi anda."}
                    </p>
                </div>
                <Button variant="outline" onClick={load}>
                    <RefreshCw className="w-4 h-4 mr-2" /> Muat Ulang
                </Button>
            </div>

            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-4 flex flex-col md:flex-row gap-2 items-start md:items-center">
                    <Select value={filter.module} onValueChange={(v) => setFilter({ ...filter, module: v })}>
                        <SelectTrigger className="w-44 h-10" data-testid="riwayat-import-module-select"><SelectValue placeholder="Menu" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="peserta">Peserta Didik</SelectItem>
                            <SelectItem value="orientasi_rs">Orientasi RS</SelectItem>
                            <SelectItem value="all">Semua Menu</SelectItem>
                        </SelectContent>
                    </Select>
                    {isSuper && (
                        <ProdiSelect
                            prodi={prodiList}
                            value={filter.prodi_id}
                            onValueChange={(v) => setFilter({ ...filter, prodi_id: v })}
                            includeAll
                            placeholder="Prodi"
                            className="w-64 h-10"
                        />
                    )}
                    <Select value={filter.phase} onValueChange={(v) => setFilter({ ...filter, phase: v })}>
                        <SelectTrigger className="w-44 h-10"><SelectValue placeholder="Tipe" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="commit">Import Final</SelectItem>
                            <SelectItem value="preview">Preview / Validasi</SelectItem>
                            <SelectItem value="all">Semua</SelectItem>
                        </SelectContent>
                    </Select>
                </CardContent>
            </Card>
            </StickyHeader>

            <Card className="border-slate-200 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full compact">
                        <thead className="bg-slate-100">
                            <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                <th>Tanggal</th>
                                <th>Diupload oleh</th>
                                {isSuper && <th>Prodi</th>}
                                <th>Nama File</th>
                                <th>Tipe</th>
                                <th className="text-right">Total Baris</th>
                                <th className="text-right">Berhasil</th>
                                <th className="text-right">Gagal</th>
                                <th className="text-right pr-4">Log Gagal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={isSuper ? 9 : 8} className="text-center py-10 text-slate-400">Memuat...</td></tr>
                            ) : logs.length === 0 ? (
                                <tr><td colSpan={isSuper ? 9 : 8} className="text-center py-10 text-slate-400">Belum ada riwayat import.</td></tr>
                            ) : logs.map((l) => {
                                const ph = PHASE_LABEL[l.phase] || PHASE_LABEL.commit;
                                return (
                                    <tr key={l.id} className="border-t border-slate-200 hover:bg-slate-50">
                                        <td className="text-xs text-slate-500 whitespace-nowrap">{formatWIB(l.uploaded_at)}</td>
                                        <td className="font-mono text-xs">{l.uploaded_by}</td>
                                        {isSuper && (
                                            <td className="text-slate-600 text-sm">
                                                {l.prodi_nama
                                                    ? `${l.prodi_jenjang ? `[${l.prodi_jenjang}] ` : ""}${l.prodi_nama}`
                                                    : (prodiMap[l.prodi_id]?.nama_prodi || "-")}
                                            </td>
                                        )}
                                        <td className="text-sm text-slate-700 max-w-[220px] truncate" title={l.filename}>{l.filename || "-"}</td>
                                        <td>
                                            <span className={`inline-block px-2 py-0.5 rounded-full border text-[11px] font-semibold ${ph.cls}`}>{ph.text}</span>
                                            {l.biodata_longgar && (
                                                <span className="block mt-1 text-[11px] font-semibold text-amber-800" data-testid={`riwayat-biodata-longgar-${l.id}`}
                                                    title="Impor Super Admin: biodata belum lengkap diizinkan">
                                                    Biodata belum lengkap diizinkan ({l.biodata_belum_lengkap_count ?? 0} ditandai)
                                                </span>
                                            )}
                                        </td>
                                        <td className="text-right font-semibold text-slate-800">{l.total_rows}</td>
                                        <td className="text-right font-semibold text-emerald-700">{l.success_count}</td>
                                        <td className="text-right font-semibold text-rose-700">{l.failed_count}</td>
                                        <td className="text-right pr-2">
                                            {l.failed_count > 0 ? (
                                                <Button size="sm" variant="outline" onClick={() => downloadFailed(l)}>
                                                    <Download className="w-3.5 h-3.5 mr-1.5" /> Download
                                                </Button>
                                            ) : (
                                                <span className="text-xs text-slate-400">—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="px-4 py-2 border-t bg-slate-50 text-xs text-slate-500">
                    Total: <span className="font-bold text-slate-800">{logs.length}</span> entri
                </div>
            </Card>
        </div>
    );
}
